const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MAINTENANCE_MIDNIGHT = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.0+)?$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const present = (value) => value !== undefined && value !== null && value !== '';

export function parseCalendarDate(value) {
  if (typeof value !== 'string') return null;
  const match = DATE_ONLY.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth[month - 1]) return null;

  return { year, month, day, dayKey: value };
}

export function parseMaintenanceBusinessDate(value) {
  if (typeof value !== 'string') return null;
  const midnight = MAINTENANCE_MIDNIGHT.exec(value);
  if (midnight) return parseCalendarDate(`${midnight[1]}-${midnight[2]}-${midnight[3]}`);
  return parseCalendarDate(value);
}

export function parseTimestamp(value) {
  if (typeof value !== 'string' || !DATE_TIME.test(value)) return null;
  if (!parseCalendarDate(value.slice(0, 10))) return null;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

function formatCalendarDate(date, options) {
  // UTC is used only as a formatting vessel. The written calendar fields are not
  // parsed as an instant, so their meaning cannot shift with the browser timezone.
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: 'UTC' })
    .format(new Date(Date.UTC(date.year, date.month - 1, date.day)));
}

function dateOnlyTemporal(raw, date, secondaryValue) {
  const secondary = parseTimestamp(secondaryValue);
  return {
    raw,
    display: formatCalendarDate(date, { month: 'short', day: 'numeric', year: 'numeric' }),
    dayKey: date.dayKey,
    secondaryMs: secondary ? secondary.getTime() : Number.NEGATIVE_INFINITY,
    groupKey: date.dayKey.slice(0, 7),
    groupLabel: formatCalendarDate(date, { month: 'long', year: 'numeric' }),
  };
}

function timestampTemporal(raw) {
  const timestamp = parseTimestamp(raw);
  if (!timestamp) return null;
  return {
    raw,
    display: new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(timestamp),
    dayKey: `${timestamp.getFullYear()}-${String(timestamp.getMonth() + 1).padStart(2, '0')}-${String(timestamp.getDate()).padStart(2, '0')}`,
    secondaryMs: timestamp.getTime(),
    groupKey: `${timestamp.getFullYear()}-${String(timestamp.getMonth() + 1).padStart(2, '0')}`,
    groupLabel: new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(timestamp),
  };
}

const unavailable = (raw = '') => ({
  raw, display: 'Date unavailable', dayKey: '', secondaryMs: Number.NEGATIVE_INFINITY,
  groupKey: 'unknown', groupLabel: 'Date unavailable',
});

export function repairTemporal(record) {
  for (const raw of [record.dateCompleted, record.dateStarted]) {
    const date = parseCalendarDate(raw);
    if (date) return dateOnlyTemporal(raw, date, record.createdAt);
  }
  return unavailable([record.dateCompleted, record.dateStarted].find(present));
}

export function maintenanceTemporal(record) {
  for (const raw of [record.dateCompleted, record.dateStarted]) {
    const date = parseMaintenanceBusinessDate(raw);
    if (date) return dateOnlyTemporal(raw, date, record.createdAt);
  }
  for (const raw of [record.performedAt, record.createdAt, record.updatedAt]) {
    const temporal = timestampTemporal(raw);
    if (temporal) return temporal;
  }
  return unavailable([
    record.dateCompleted, record.dateStarted, record.performedAt, record.createdAt, record.updatedAt,
  ].find(present));
}

export function checklistTemporal(record) {
  return timestampTemporal(record.createdAt) || unavailable(record.createdAt);
}
